import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvGet, dvCreate, dvUpdate, dvDelete } from '../api/dataverseClient';
import { Project } from '../types';
import { createProjectMember, ProjectMemberRole } from './projectMemberService';

const PROJECT_FIELDS = [
  'dagrun_pm_projectid',
  'dagrun_pm_name',
  'dagrun_pm_description',
  'dagrun_pm_lifecyclestatus',
  'dagrun_pm_methodologymode',
  'dagrun_pm_percentcomplete',
  'dagrun_pm_healthrag',
  'dagrun_pm_m365groupid',
  'dagrun_pm_teamsteamid',
  'dagrun_pm_loopworkspaceid',
  'dagrun_pm_currency',
  'dagrun_pm_isbillable',
  'dagrun_pm_openissuescount',
  'dagrun_pm_openriskscount',
  'dagrun_pm_nextmilestonedate',
  'dagrun_pm_ownerid',
  'dagrun_pm_ownername',
  'dagrun_pm_baseline_label',
  'dagrun_pm_baseline_capturedon',
  'dagrun_pm_resourcepoolid',
  'dagrun_pm_useglobalresourcepool',
  'dagrun_pm_module_issues',
  'dagrun_pm_module_risks',
  'dagrun_pm_module_milestones',
  'dagrun_pm_module_timetracking',
  'dagrun_pm_module_budget',
  'dagrun_pm_module_resources',
  'dagrun_pm_module_changemanagement',
  'dagrun_pm_module_meetingintel',
  'dagrun_pm_module_clientportal',
  'dagrun_pm_module_ai',
  'dagrun_pm_module_agile',
  'dagrun_pm_module_goals',
  // These five were missing from the select list — so although the
  // Settings toggles PATCHed them correctly, the project reload never
  // fetched them back and the toggle rendered off on every refresh.
  'dagrun_pm_module_training',
  'dagrun_pm_module_punchlist',
  'dagrun_pm_module_stakeholders',
  'dagrun_pm_module_submittalrfi',
  'dagrun_pm_module_lessons',
  'dagrun_pm_sharepointsiteurl',
];

export async function listProjects(pca: IPublicClientApplication): Promise<Project[]> {
  return dvList<Project>(pca, 'dagrun_pm_projects', {
    select: PROJECT_FIELDS,
    orderby: 'dagrun_pm_name asc',
    top: 100,
  });
}

export async function getProject(pca: IPublicClientApplication, id: string): Promise<Project> {
  return dvGet<Project>(pca, 'dagrun_pm_projects', id, { select: PROJECT_FIELDS });
}

export interface CreateProjectInput {
  name: string;
  description?: string | null;
  methodology?: number;        // picklist value, defaults to Kanban
  useGlobalResourcePool?: boolean;
  resourcePoolId?: string | null;
  // Creator identity captured at creation time (AAD OID — never a name/email
  // match). When present, the creator is set as project owner AND auto-added as
  // a ProjectManager-role member so every new project has a guaranteed PM.
  creator?: { userId: string; userName: string; userEmail?: string | null };
  // Optional M365 Group to link at creation (existing group id). Group creation
  // (POST /groups → PUT team) is done by the caller before this, then its id
  // passed here; kept optional so Skip remains the default.
  m365GroupId?: string | null;
  modules?: Partial<{
    issues:            boolean;
    risks:             boolean;
    milestones:        boolean;
    timetracking:      boolean;
    budget:            boolean;
    resources:         boolean;
    changemanagement:  boolean;
    meetingintel:      boolean;
    clientportal:      boolean;
    ai:                boolean;
    agile:             boolean;
    goals:             boolean;
    training:          boolean;
    punchlist:         boolean;
    stakeholders:      boolean;
    submittalrfi:      boolean;
    lessons:           boolean;
  }>;
}

export async function createProject(
  pca: IPublicClientApplication,
  input: string | CreateProjectInput,
): Promise<string> {
  // Back-compat: accept (pca, name) as well as (pca, input).
  const inp: CreateProjectInput = typeof input === 'string' ? { name: input } : input;

  const data: Record<string, unknown> = {
    dagrun_pm_name: inp.name,
    dagrun_pm_lifecyclestatus: 100000000, // Draft
    dagrun_pm_methodologymode: inp.methodology ?? 100000002, // Kanban default
  };
  if (inp.creator?.userId) {
    data.dagrun_pm_ownerid = inp.creator.userId;
    data.dagrun_pm_ownername = inp.creator.userName;
  }
  if (inp.m365GroupId) data.dagrun_pm_m365groupid = inp.m365GroupId;
  if (inp.description && inp.description.trim().length > 0) {
    data.dagrun_pm_description = inp.description.trim();
  }
  if (inp.useGlobalResourcePool) {
    data.dagrun_pm_useglobalresourcepool = true;
    if (inp.resourcePoolId) data.dagrun_pm_resourcepoolid = inp.resourcePoolId;
  }
  const m = inp.modules ?? {};
  data.dagrun_pm_module_issues            = m.issues            ?? false;
  data.dagrun_pm_module_risks             = m.risks             ?? false;
  data.dagrun_pm_module_milestones        = m.milestones        ?? false;
  data.dagrun_pm_module_timetracking      = m.timetracking      ?? false;
  data.dagrun_pm_module_budget            = m.budget            ?? false;
  data.dagrun_pm_module_resources         = m.resources         ?? false;
  data.dagrun_pm_module_changemanagement  = m.changemanagement  ?? false;
  data.dagrun_pm_module_meetingintel      = m.meetingintel      ?? false;
  data.dagrun_pm_module_clientportal      = m.clientportal      ?? false;
  data.dagrun_pm_module_ai                = m.ai                ?? false;
  data.dagrun_pm_module_agile             = m.agile             ?? false;
  data.dagrun_pm_module_goals             = m.goals             ?? false;
  data.dagrun_pm_module_training          = m.training          ?? false;
  data.dagrun_pm_module_punchlist         = m.punchlist         ?? false;
  data.dagrun_pm_module_stakeholders      = m.stakeholders      ?? false;
  data.dagrun_pm_module_submittalrfi      = m.submittalrfi      ?? false;
  data.dagrun_pm_module_lessons           = m.lessons           ?? false;

  const projectId = await dvCreate(pca, 'dagrun_pm_projects', data);

  // Guaranteed PM: auto-add the creator as a ProjectManager-role member using
  // their real AAD OID (the mismatch that caused the 2026-07-06 bug came from
  // matching on name/email — this always uses the OID captured at creation).
  // Awaited so a failure surfaces; the project already exists, so we still
  // return its id (the Team & Roles section + enforce-one-PM guard recover a
  // rare miss) but log loudly rather than swallow silently.
  if (inp.creator?.userId) {
    try {
      await createProjectMember(pca, {
        projectId,
        userId: inp.creator.userId,
        userName: inp.creator.userName,
        userEmail: inp.creator.userEmail ?? null,
        role: ProjectMemberRole.ProjectManager,
        isExternal: false,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('createProject: creator PM member creation failed — project has no PM yet', e);
    }
  }

  // Auto-provision a single default "To Do" collection so the board has somewhere to render.
  try {
    await dvCreate(pca, 'dagrun_pm_collections', {
      dagrun_pm_name: 'To Do',
      dagrun_pm_sortorder: 1,
      dagrun_pm_isdonecollection: false,
      'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${projectId})`,
    });
  } catch {
    // Non-fatal.
  }
  return projectId;
}

export async function updateProject(
  pca: IPublicClientApplication,
  id: string,
  patch: Record<string, unknown>
): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_projects', id, patch);
}

// Returns a Map of projectId -> average task completion across that
// project's tasks. Effective per-task pct treats status=Done as 100%
// regardless of the stored dagrun_pm_percentcomplete (which is 0 by
// default, even after a user has dragged a card to a Done collection).
// Without this rule the aggregate read 0% for projects whose tasks are
// fully done by status but never had % set explicitly.
//
// Projects with no tasks are absent from the map — callers default to 0%.
const TASK_DONE_STATUS = 100000003;

export async function getProjectsAveragePercentComplete(
  pca: IPublicClientApplication,
): Promise<Map<string, number>> {
  try {
    const rows = await dvList<{
      _dagrun_pm_projectid_value: string | null;
      dagrun_pm_percentcomplete: number | null;
      dagrun_pm_status: number | null;
    }>(pca, 'dagrun_pm_tasks', {
      select: ['_dagrun_pm_projectid_value', 'dagrun_pm_percentcomplete', 'dagrun_pm_status'],
      top: 5000,
    });
    const sums = new Map<string, { total: number; count: number }>();
    rows.forEach(r => {
      const pid = r._dagrun_pm_projectid_value;
      if (!pid) return;
      const stored = typeof r.dagrun_pm_percentcomplete === 'number' ? r.dagrun_pm_percentcomplete : 0;
      const pct = r.dagrun_pm_status === TASK_DONE_STATUS ? 100 : stored;
      const entry = sums.get(pid) ?? { total: 0, count: 0 };
      entry.total += pct;
      entry.count += 1;
      sums.set(pid, entry);
    });
    const out = new Map<string, number>();
    sums.forEach((v, pid) => { if (v.count > 0) out.set(pid, Math.round(v.total / v.count)); });
    return out;
  } catch {
    return new Map();
  }
}

export async function getProjectAveragePercentComplete(
  pca: IPublicClientApplication,
  projectId: string,
): Promise<number> {
  try {
    const rows = await dvList<{ dagrun_pm_percentcomplete: number | null; dagrun_pm_status: number | null }>(
      pca, 'dagrun_pm_tasks', {
        select: ['dagrun_pm_percentcomplete', 'dagrun_pm_status'],
        filter: `_dagrun_pm_projectid_value eq ${projectId}`,
        top: 5000,
      },
    );
    if (rows.length === 0) return 0;
    const sum = rows.reduce((acc, r) => {
      const stored = typeof r.dagrun_pm_percentcomplete === 'number' ? r.dagrun_pm_percentcomplete : 0;
      return acc + (r.dagrun_pm_status === TASK_DONE_STATUS ? 100 : stored);
    }, 0);
    return Math.round(sum / rows.length);
  } catch {
    return 0;
  }
}

export async function updateProjectStatus(
  pca: IPublicClientApplication,
  id: string,
  status: number
): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_projects', id, { dagrun_pm_lifecyclestatus: status });
}

// Capture a project baseline: snapshot dagrun_pm_startdate + dagrun_pm_duedate
// onto dagrun_pm_baselinestart + dagrun_pm_baselineend for every task that
// has at least one scheduled date. Then PATCH the project with the new label
// + captured timestamp. Tasks without dates are skipped (no baseline to set).
export async function captureProjectBaseline(
  pca: IPublicClientApplication,
  projectId: string,
  label: string,
): Promise<{ tasksSnapshotted: number }> {
  // Minimal field fetch — avoid pulling description/big columns we don't need.
  const tasks = await dvList<{
    dagrun_pm_taskid: string;
    dagrun_pm_startdate: string | null;
    dagrun_pm_duedate: string | null;
  }>(pca, 'dagrun_pm_tasks', {
    select: ['dagrun_pm_taskid', 'dagrun_pm_startdate', 'dagrun_pm_duedate'],
    filter: `_dagrun_pm_projectid_value eq ${projectId}`,
    top: 5000,
  });

  let count = 0;
  // Sequential PATCH — simple and reliable against Dataverse throttling.
  // For very large projects a $batch could cap wall-time; sequential is
  // sufficient up to a few hundred tasks.
  for (const t of tasks) {
    if (!t.dagrun_pm_startdate && !t.dagrun_pm_duedate) continue;
    await dvUpdate(pca, 'dagrun_pm_tasks', t.dagrun_pm_taskid, {
      dagrun_pm_baselinestart: t.dagrun_pm_startdate,
      dagrun_pm_baselineend:   t.dagrun_pm_duedate,
    });
    count++;
  }

  await dvUpdate(pca, 'dagrun_pm_projects', projectId, {
    dagrun_pm_baseline_label: label,
    dagrun_pm_baseline_capturedon: new Date().toISOString(),
  });

  return { tasksSnapshotted: count };
}

// Cascade-delete is configured on the 14 core child relationships
// (tasks, collections, phases, issues, risks, milestones, decisions,
// sprints, standupentries, retrospectives, timeentries, budgets,
// expenses, dependencies). Dataverse will remove the project's child
// rows in those tables atomically when the project row is deleted.
export async function deleteProject(
  pca: IPublicClientApplication,
  projectId: string,
): Promise<void> {
  return dvDelete(pca, 'dagrun_pm_projects', projectId);
}
